import { changeLanguage } from "@services/Language";
import { secureStorage, StorageKeys } from "@services/secureStorage.service";
import { validateConfig, validateLanguage } from "@utilities/appUtils";
import { useCallback, useEffect, useRef, useState } from "react";

export const EVENTS_TYPES = {
  WIDGET_INIT: "WIDGET_INIT",
  APP_READY: "APP_READY",
  UPDATE_LANGUAGE: "UPDATE_LANGUAGE",
  UPDATE_JWT: "UPDATE_JWT",
  CHILD_ROUTE_CHANGE: "CHILD_ROUTE_CHANGE",
  PARENT_NAVIGATE: "PARENT_NAVIGATE",
} as const;

export interface ParentConfig {
  jwt: string;
  userId: string;
  clientId: string;
  appId: string;
  language?: string;
}

interface UseParentCommunicationReturn {
  isLoading: boolean;
  error: string | null;
  sendMessage: (type: string, payload?: unknown) => void;
  isInIframe: boolean;
  showDevConfig: boolean;
  handleDevConfigSubmit: (config: ParentConfig) => void;
  authData: ParentConfig | null;
}


// Helper: Sync auth config to storage
const syncAuthToStorage = (config: ParentConfig) => {
  secureStorage.setMultiple({
    [StorageKeys.JWT]: config.jwt,
    [StorageKeys.CLIENT_ID]: config.clientId,
    [StorageKeys.USER_ID]: config.userId,
    [StorageKeys.APP_ID]: config.appId,
  });

  if (config.language) {
    const validLanguage = validateLanguage(config.language);
    secureStorage.set(StorageKeys.LANGUAGE, validLanguage);
    changeLanguage(validLanguage);
  }
};

export const useParentCommunication = (
  timeout: number = 10000,
): UseParentCommunicationReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authData, setAuthData] = useState<ParentConfig | null>(null);
  const isInIframe = globalThis.self !== globalThis.top;
  const loadingRef = useRef(isLoading);

  useEffect(() => {
    loadingRef.current = isLoading;
  }, [isLoading]);

  const sendMessage = useCallback((type: string, payload?: unknown) => {
    if (isInIframe) {
      globalThis.parent.postMessage({ type, payload }, "*");
    }
  }, [isInIframe]);

  const handleDevConfigSubmit = useCallback((config: ParentConfig) => {
    syncAuthToStorage(config);
    setAuthData(config);
    // Keep loading true - AppInitializer will show loading until config fetches
  }, []);

  useEffect(() => {
    if (!isInIframe) return;

    let timeoutId: NodeJS.Timeout;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === EVENTS_TYPES.WIDGET_INIT) {
        if (validateConfig(event.data)) {
          const { jwt, userId, clientId, appId, language } = event.data;
          const authConfig = { jwt, userId, clientId, appId, language };
          
          syncAuthToStorage(authConfig);
          setAuthData(authConfig);
          setIsLoading(false);
          clearTimeout(timeoutId);
        } else {
          setError("Invalid configuration received from parent");
          setIsLoading(false);
        }
      }
    };

    sendMessage(EVENTS_TYPES.APP_READY);
    globalThis.addEventListener("message", handleMessage);
    
    timeoutId = setTimeout(() => {
      if (loadingRef.current) {
        setError("Initialization timeout: No response from parent");
        setIsLoading(false);
      }
    }, timeout);

    return () => {
      globalThis.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);
    };
  }, [isInIframe, sendMessage, timeout]);

  // Derive showDevConfig: show if not in iframe, loading, and no auth data yet
  const showDevConfig = !isInIframe && isLoading && !authData;

  return { 
    isLoading, 
    error, 
    sendMessage, 
    isInIframe, 
    showDevConfig, 
    handleDevConfigSubmit,
    authData
  };
};
